import React from 'react'
import classnames from 'classnames'
import type { CollectionsInvoice } from '../../../apis/types'

export function hasDistinctLateByDate(rows: CollectionsInvoice[]): boolean {
  return rows.some((row) => row.lateBy != null && row.lateBy !== row.due)
}

export interface StatementTypeVisibility {
  showKing: boolean
  showUtility: boolean
  showDob: boolean
  showOptiRate: boolean
}

export function getStatementTypeVisibility(
  rows: CollectionsInvoice[],
  dobAvailability: Record<string, boolean>,
  isPgeInvoice: (row: CollectionsInvoice) => boolean,
  hasTenantContextForDob: boolean
): StatementTypeVisibility {
  return {
    showKing: true,
    showUtility: rows.some((row) => row.externalStatementAvailable),
    showDob: hasTenantContextForDob && rows.some((row) => isPgeInvoice(row) && dobAvailability[row.id]),
    showOptiRate: rows.some((row) => row.hasOptiRateAudit),
  }
}

export function countStatementSlots(visibility: StatementTypeVisibility): number {
  return [visibility.showKing, visibility.showUtility, visibility.showDob, visibility.showOptiRate].filter(Boolean).length
}

const STATEMENT_ICON_MIN_WIDTH = 28 // min-w-[1.75rem]
const STATEMENT_ICON_GAP = 4 // gap-1
const STATEMENT_CELL_HORIZONTAL_PADDING = 32 // default Table cell p-4 (16px × 2)
const STATEMENT_ICON_RING = 4 // ring-1 plus a little room so the border is not clipped

export function statementColumnWidthFor(visibility: StatementTypeVisibility): number {
  const slots = countStatementSlots(visibility)
  if (slots === 0) {
    return 0
  }
  const iconsWidth = slots * STATEMENT_ICON_MIN_WIDTH + Math.max(slots - 1, 0) * STATEMENT_ICON_GAP
  return iconsWidth + STATEMENT_CELL_HORIZONTAL_PADDING + STATEMENT_ICON_RING
}

export function StatementIconButton({
  label,
  title,
  available,
  onClick,
}: {
  label: string
  title: string
  available: boolean
  onClick?: () => void
}): React.ReactElement {
  return (
    <button
      type="button"
      title={available ? title : `${title} (not available)`}
      disabled={!available}
      onClick={available ? onClick : undefined}
      className={classnames(
        'inline-flex h-7 min-w-[1.75rem] shrink-0 items-center justify-center rounded px-1 text-[11px] font-semibold leading-none',
        available
          ? 'cursor-pointer bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200 hover:bg-indigo-100 dark:bg-indigo-950 dark:text-indigo-300 dark:ring-indigo-800 dark:hover:bg-indigo-900'
          : 'cursor-not-allowed bg-slate-50 text-slate-300 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-600 dark:ring-slate-700'
      )}
    >
      {label}
    </button>
  )
}

export interface InvoiceStatementIconsProps {
  invoice: CollectionsInvoice
  visibility: StatementTypeVisibility
  isPgeInvoice: boolean
  dobAvailable: boolean
  onOpenKing: () => void
  onOpenUtility: () => void
  onDownloadDob: () => void
  onOpenOptiRate: () => void
}

export function InvoiceStatementIcons({
  invoice,
  visibility,
  isPgeInvoice,
  dobAvailable,
  onOpenKing,
  onOpenUtility,
  onDownloadDob,
  onOpenOptiRate,
}: InvoiceStatementIconsProps): React.ReactElement {
  return (
    <div className="flex items-center gap-1">
      {visibility.showKing && <StatementIconButton label="K" title="King statement" available onClick={onOpenKing} />}
      {visibility.showUtility && (
        <StatementIconButton
          label="U"
          title="Utility statement"
          available={invoice.externalStatementAvailable}
          onClick={onOpenUtility}
        />
      )}
      {visibility.showDob && (
        <StatementIconButton
          label="D"
          title="Download DOB"
          available={isPgeInvoice && dobAvailable}
          onClick={onDownloadDob}
        />
      )}
      {visibility.showOptiRate && (
        <StatementIconButton
          label="O"
          title="OptiRate audit"
          available={Boolean(invoice.hasOptiRateAudit)}
          onClick={onOpenOptiRate}
        />
      )}
    </div>
  )
}
